import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";

import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";

import type { LeadAssignmentRequest, LeadAssignmentResponse, AgentWorkloadResponse } from "../types/leadassign-types";
import { DEFAULT_PAGE_SIZE, buildUnassignedLeadsParams, type UnassignedLeadsFilters } from "./leadAssignmentParams";
export { buildUnassignedLeadsParams, type UnassignedLeadsFilters };

interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  leadStat?: string;
  lastCall?: string;
  createdAt: string;
  state?: string;
  assignedToUserId?: string;
  assignedToName?: string;
  hasHousehold?: boolean;
  householdMemberCount?: number;
  listName?: string;
  uploadedByName?: string;
  /** Has a live policy: unassign keeps it with its agent unless sold/closed are included. */
  hasPolicy?: boolean;
  /** Next upcoming appointment (ISO) and who it's with. */
  nextAppointmentAt?: string | null;
  nextAppointmentWith?: string | null;
}


// Cache the server total across pages so pagination stays correct
// (backend only returns totalItems on page 1 for performance)
let cachedServerTotal: Record<string, number> = {};

export const useUnassignedLeads = (filters?: UnassignedLeadsFilters) => {
  const { user } = useUserContext();
  const currentAgencyId = user.currentAgency?.agency?.id;
  const agencyRole = user.currentAgency?.role;
  const userType = user?.user?.userType;
  const isOwnerOrAdmin = agencyRole === 'owner' || agencyRole === 'agency_owner' || agencyRole === 'admin' || userType === 'admin';

  return useQuery({
    // The whole filter object, so a new filter can't be left out of the key
    // (the age range was: typing an age never refetched).
    queryKey: ["unassignedLeads", currentAgencyId, filters],
    // No try/catch: a failed or timed-out request must reach the component as an
    // error. Returning an empty list here rendered "No leads match the selected
    // filters" for a request that never finished (Mark Dusevic, 2026-09-19).
    queryFn: async (): Promise<{ unassignedLeads: Lead[]; total?: number; totalPages?: number }> => {
      const queryParams = buildUnassignedLeadsParams(filters, isOwnerOrAdmin);
      const pageSize = filters?.limit || DEFAULT_PAGE_SIZE;

      const result = await handleApiCall(async () => {
        const response = await api.get({
          url: getApiUrl(endpoint.leadAssignment.getUnassignedLeads) + `?${queryParams.toString()}`,
          auth: true,
        });
        return response;
      });

      const responseData = (result as any).data || result;
      const leads = responseData.leads || responseData.items || [];
      const meta = responseData.meta || {};

      // Backend only returns totalItems on page 1 (skips COUNT on later pages).
      // Cache it per FULL filter set: the old key left out timezone, city,
      // carrier and custom fields, so page 2 could show another filter's total.
      const { page: _page, limit: _limit, ...filterKey } = filters || {};
      const cacheKey = `${currentAgencyId}-${JSON.stringify(filterKey)}`;
      if (meta.totalItems != null) {
        cachedServerTotal[cacheKey] = meta.totalItems;
      }
      const serverTotal = meta.totalItems || cachedServerTotal[cacheKey] || responseData.total || leads.length;

      // Map leads to consistent format
      const unassignedLeads: Lead[] = Array.isArray(leads) ? leads.map((lead: any) => ({
        id: lead.id,
        name: lead.name || `${lead.firstName || ""} ${lead.lastName || ""}`.trim(),
        email: lead.email || "",
        phone: lead.phone || "",
        status: lead.status || "new",
        leadStat: lead.leadStat || lead.lead_stat || "New lead",
        lastCall: lead.lastCall || lead.last_call || null,
        createdAt: lead.createdAt || lead.created_at || new Date().toISOString(),
        state: lead.state || undefined,
        assignedToUserId: lead.assignedToUserId || lead.assigned_to_user_id || undefined,
        assignedToName: lead.assignedToName || lead.assigned_to_name || lead.assignedByName || lead.assigned_by_name || undefined,
        hasHousehold: lead.hasHousehold || lead.has_household || false,
        householdMemberCount: lead.householdMemberCount || lead.household_member_count || 0,
        listName: lead.listName || lead.list_name || undefined,
        activityVisibleAfter: lead.activityVisibleAfter || lead.activity_visible_after || undefined,
      })) : [];

      return {
        unassignedLeads,
        total: serverTotal,
        totalPages: Math.ceil(serverTotal / pageSize),
      };
    },
    enabled: !!currentAgencyId,
    placeholderData: keepPreviousData, // Keep old data visible while new filter/page loads
    staleTime: 0, // Always refetch on mount/navigation — shows fresh data when coming from leads page
    gcTime: 5 * 60 * 1000,
    refetchInterval: 120 * 1000, // 120s background poll
    refetchOnMount: 'always', // Refetch every time component mounts (page navigation)
    refetchOnWindowFocus: true, // Refetch when switching back from another tab
    // Don't retry a timeout: the client gives up at 60s, so 2 retries meant 3
    // minutes of spinner before the user learned anything.
    retry: (failureCount, error: any) =>
      failureCount < 2 && error?.code !== 'ECONNABORTED' && !/timeout/i.test(String(error?.message || '')),
    retryDelay: (attemptIndex) => Math.min(2000 * (attemptIndex + 1), 5000),
  });
};

export const useAgentWorkload = () => {
  const { user } = useUserContext();
  // Extract stable primitive to prevent React #310 infinite loop
  const currentAgencyId = user.currentAgency?.agency?.id;

  return useQuery({
    queryKey: ["agentWorkload", currentAgencyId],
    queryFn: async (): Promise<AgentWorkloadResponse> => {
      try {
        if (!currentAgencyId) {
          throw new Error("No agency selected");
        }

        // Request all agents (no pagination limit) for the dropdown
        // Backend defaults to page=1, limit=10, so we need to request a high limit to get all
        const url = getApiUrl(endpoint.agency.getAgentsWorkload(currentAgencyId)) + '?limit=1000';
        
        const result = await handleApiCall(async () => {
          const response = await api.get({
            url,
            auth: true,
          });
          return response;
        });

        // handleApiCall may return the response directly or wrapped
        // Backend structure: { statusCode, message, data: { items: [...], meta: {...} }, timestamp }

        // Try multiple paths to extract data
        let data: any = {};
        let agents: any[] = [];
        
        if (Array.isArray(result)) {
          // If result is directly an array, use it
          agents = result;
        } else if (result?.data?.data?.items) {
          // Nested: result.data.data.items
          data = result.data.data;
          agents = data.items || [];
        } else if (result?.data?.items) {
          // Standard: result.data.items
          data = result.data;
          agents = data.items || [];
        } else if (result?.data && Array.isArray(result.data)) {
          // Direct array in data
          agents = result.data;
        } else if (result?.items) {
          // Items at root level
          agents = result.items;
        } else {
          // Fallback: try to find any array in the response
          data = result?.data || result || {};
          agents = data.items || data.agents || (Array.isArray(data) ? data : []);
        }

        // Helper to format response time from number (seconds) to string like "2m"
        const formatResponseTime = (time: any): string => {
          if (typeof time === 'string') return time;
          if (typeof time === 'number') {
            if (time === 0) return '0m';
            if (time < 60) return `${Math.round(time)}s`;
            return `${Math.round(time / 60)}m`;
          }
          return '0m';
        };

        // Backend returns agents with 'id' field (user ID) which is what we need for assignment
        // Map to ensure consistent structure across components
        const mappedAgents = agents.map((agent: any) => {
          // Backend returns agent.id (user ID) - this is what assignLeadToAgent expects
          const userId = agent.id || agent.userId || agent.agentId;

          return {
            id: userId,  // User ID - primary key for AgentWorkload/AgentPerformance
            agentId: userId,  // User ID - for AgentAssignmentDropdown and assignment API
            name: agent.name || agent.agentName || 'Unknown Agent',  // For AgentWorkload/AgentPerformance
            agentName: agent.name || agent.agentName || 'Unknown Agent',  // For AgentAssignmentDropdown
            email: agent.email || '',
            agencyId: agent.agencyId || undefined,  // Agency ID for server-side sub-agency filtering
            agencyName: agent.agencyName || undefined,  // Agency name for sub-agency identification
            currentLeads: agent.currentLeads || agent.activeLeadsCount || 0,  // For AgentWorkload
            activeLeadsCount: agent.currentLeads || agent.activeLeadsCount || 0,  // For AgentAssignmentDropdown
            ownedLeads: agent.ownedLeads ?? 0,  // Agent's own uploaded leads (user_id), for both workload card + assign dropdown
            workedLeads: agent.workedLeads ?? 0,  // Distinct leads touched by any channel; the card's "Worked on"
            sharedBookLeads: agent.sharedBookLeads ?? 0,  // Owner/admin shared book, never summed across the roster
            completedLeads: agent.completedLeads || 0,  // For AgentWorkload progress bar
            maxLeads: agent.maxLeads || 0,
            leadGoal: agent.leadGoal ?? undefined,  // Custom lead goal from agency_users - use ?? to preserve 0
            goalType: agent.goalType ?? null,        // Configurable goal metric
            goalValue: agent.goalValue ?? null,      // Target for the goal metric
            goalDuration: agent.goalDuration ?? null,// daily | weekly | monthly
            goalCurrent: agent.goalCurrent ?? null,  // Current value of the metric in its window
            status: agent.status || 'active',
            lastActivity: agent.lastActivity || agent.lastActivityAt || undefined,  // Don't default to current time
            conversionRate: agent.conversionRate || 0,
            avgResponseTime: formatResponseTime(agent.avgResponseTime),
            assignedBreakdown: Array.isArray(agent.assignedBreakdown) ? agent.assignedBreakdown : [],
          };
        });

        return { agents: mappedAgents };
      } catch (error: any) {
        // Error handling - log to monitoring service in production
        return { agents: [] };
      }
    },
    enabled: !!currentAgencyId,
    placeholderData: keepPreviousData, // Keep old data visible while refetching
    staleTime: 0, // Always refetch on mount/navigation
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 60 * 1000, // 60s background poll
    refetchOnMount: 'always', // Refetch every time component mounts
    refetchOnWindowFocus: true, // Refetch when switching back
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(2000 * (attemptIndex + 1), 5000),
  });
};

// Hook for fetching ALL leads (assigned and unassigned) without owner filtering
// Used by AgentWorkload/AgentPerformance to show assigned leads when agent row is expanded.
// LAZY: Only fetches when `enabled` is true (pass enabled: !!expandedAgentId).
// This prevents the heavy 2000-lead query from running on every page load / poll cycle.
export const useAllLeads = (options?: { enabled?: boolean }) => {
  const { user } = useUserContext();
  const currentAgencyId = user.currentAgency?.agency?.id;
  const shouldFetch = options?.enabled !== false; // default true for backwards compat

  return useQuery({
    queryKey: ["allLeads", currentAgencyId],
    queryFn: async (): Promise<{ leads: Lead[] }> => {
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('skipOwnerFilter', 'true');
        queryParams.append('includeAssigned', 'true');
        queryParams.append('page', '1');
        queryParams.append('limit', '500'); // Reduced from 2000 — covers most agencies

        const result = await handleApiCall(async () => {
          const response = await api.get({
            url: getApiUrl(endpoint.leadAssignment.getUnassignedLeads) + `?${queryParams.toString()}`,
            auth: true,
          });
          return response;
        });

        const responseData = (result as any).data || result;
        const leads = responseData.leads || responseData.items || [];

        const mappedLeads: Lead[] = Array.isArray(leads) ? leads.map((lead: any) => ({
          id: lead.id,
          name: lead.name || `${lead.firstName || ""} ${lead.lastName || ""}`.trim(),
          email: lead.email || "",
          phone: lead.phone || "",
          status: lead.status || "new",
          leadStat: lead.leadStat || lead.lead_stat || "New lead",
          lastCall: lead.lastCall || lead.last_call || null,
          state: lead.state || undefined,
          createdAt: lead.createdAt || lead.created_at || new Date().toISOString(),
          assignedToUserId: lead.assignedToUserId || lead.assigned_to_user_id || undefined,
          assignedToName: lead.assignedToName || lead.assigned_to_name || lead.assignedByName || lead.assigned_by_name || undefined,
        })) : [];

        return { leads: mappedLeads };
      } catch (error) {
        return { leads: [] };
      }
    },
    enabled: !!currentAgencyId && shouldFetch,
    staleTime: 60 * 1000, // 60s — this data doesn't change rapidly
    gcTime: 5 * 60 * 1000,
    refetchInterval: false, // No auto-polling — only fetches on demand when agent expanded
    refetchOnMount: false, // Only fetch when enabled flips to true
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(2000 * (attemptIndex + 1), 5000),
  });
};

/** Page size for the expanded agent-leads view. Small page = never loads
 *  thousands at once (which OOM'd the browser), and the user pages through. */
export const AGENT_LEADS_PAGE_SIZE = 50;

/**
 * Fetch ONE PAGE of leads assigned to a specific agent, server-side filtered.
 * Replaces both: (a) the `useAllLeads` "fetch 500 agency-wide + filter client-side"
 * pattern, which MISSED an agent's older leads (>500-row window) → "didn't show all";
 * and (b) the old limit=2000 single shot, which truncated agents with more and
 * loaded thousands of rows at once. Now: server filters to the agent, 50/page,
 * paginated — shows every lead (page through) and never stresses the browser.
 */
/** Server-side filters supported by useAgentLeads — same query params the
 *  lead-assignment page's filter system sends to /lead-assignment/unassigned. */
export interface AgentLeadsFilters {
  states?: string;    // comma-separated
  timezone?: string;  // EST|CST|MST|PST|AKST|HST
  status?: string;    // disposition / leadStat
  listName?: string;
  leadName?: string;  // server-side name search
  city?: string;
  carrier?: string;
  householdPrimaries?: boolean;
  /**
   * "Worked on" mode: instead of leads ASSIGNED to the agent, list the leads
   * they actually worked — call, note, SMS, appointment or door knock. Setters
   * on a shared book have ~0 assigned, so this is the only view that shows
   * what they've really been doing.
   */
  workedByAgentId?: string;
}

export const useAgentLeads = (
  agentId?: string,
  page: number = 1,
  options?: { enabled?: boolean; filters?: AgentLeadsFilters },
) => {
  const { user } = useUserContext();
  const currentAgencyId = user.currentAgency?.agency?.id;
  const shouldFetch = options?.enabled !== false && !!agentId;
  const f = options?.filters;

  return useQuery({
    queryKey: ["agentLeads", currentAgencyId, agentId, page, f?.states, f?.timezone, f?.status, f?.listName, f?.leadName, f?.city, f?.carrier, f?.householdPrimaries, f?.workedByAgentId],
    queryFn: async (): Promise<{ leads: Lead[]; totalItems?: number; totalPages?: number }> => {
      try {
        const queryParams = new URLSearchParams();
        // Worked-on mode intentionally omits filterAgentId: these leads are
        // mostly assigned to someone else (the owner whose book they work).
        if (f?.workedByAgentId) {
          queryParams.append('workedByAgentId', f.workedByAgentId);
        } else {
          queryParams.append('filterAgentId', agentId!);
        }
        queryParams.append('includeAssigned', 'true');
        queryParams.append('skipOwnerFilter', 'true');
        // full book: keep DNC + household-member leads so rows/total match
        // the agent's real assigned count (assignment page hides those)
        queryParams.append('includeAll', 'true');
        queryParams.append('page', String(page));
        queryParams.append('limit', String(AGENT_LEADS_PAGE_SIZE));
        if (f?.states) queryParams.append('states', f.states);
        if (f?.timezone) queryParams.append('timezone', f.timezone);
        if (f?.status) queryParams.append('status', f.status);
        if (f?.listName) queryParams.append('listName', f.listName);
        if (f?.leadName) queryParams.append('leadName', f.leadName);
        if (f?.city) queryParams.append('city', f.city);
        if (f?.carrier) queryParams.append('carrier', f.carrier);
        if (f?.householdPrimaries) queryParams.append('householdPrimaries', 'true');

        const result = await handleApiCall(async () => {
          const response = await api.get({
            url: getApiUrl(endpoint.leadAssignment.getUnassignedLeads) + `?${queryParams.toString()}`,
            auth: true,
          });
          return response;
        });

        const responseData = (result as any).data || result;
        const leads = responseData.leads || responseData.items || [];
        const meta = responseData.meta || {};

        const mappedLeads: Lead[] = Array.isArray(leads) ? leads.map((lead: any) => ({
          id: lead.id,
          name: lead.name || `${lead.firstName || ""} ${lead.lastName || ""}`.trim(),
          email: lead.email || "",
          phone: lead.phone || "",
          status: lead.status || "new",
          leadStat: lead.leadStat || lead.lead_stat || "New lead",
          lastCall: lead.lastCall || lead.last_call || null,
          state: lead.state || undefined,
          createdAt: lead.createdAt || lead.created_at || new Date().toISOString(),
          assignedToUserId: lead.assignedToUserId || lead.assigned_to_user_id || undefined,
          assignedToName: lead.assignedToName || lead.assigned_to_name || lead.assignedByName || lead.assigned_by_name || undefined,
          listName: lead.listName || lead.list_name || undefined,
        })) : [];

        // totalItems/totalPages only come back on page 1 (count skipped after that
        // for performance). Callers preserve the page-1 total for the controls.
        return { leads: mappedLeads, totalItems: meta.totalItems, totalPages: meta.totalPages };
      } catch {
        return { leads: [] };
      }
    },
    enabled: !!currentAgencyId && shouldFetch,
    // keepPreviousData — page transitions don't flash empty while the next page loads
    placeholderData: (previousData: any) => previousData,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    // 60s background poll keeps the Assigned tab live; placeholderData still
    // prevents page-transition flashes, and refetchOnMount stays off so tab
    // switches don't refire instantly.
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * (attemptIndex + 1), 3000),
  });
};

export const useAssignLeads = () => {
  const queryClient = useQueryClient();
  const { user } = useUserContext();

  return useMutation<LeadAssignmentResponse, Error, { leadIds: string[]; agentId: string; assignedAgencyId: string }>({
    mutationFn: async (data: { leadIds: string[]; agentId: string; assignedAgencyId: string }) => {
      try {
        const assignmentData: LeadAssignmentRequest = {
          leadIds: data.leadIds,
          agentId: data.agentId,
          assignedAgencyId: data.assignedAgencyId,
        };

        const result = await handleApiCall(async () => {
          const response = await api.post({
            url: getApiUrl(endpoint.leadAssignment.assignLead),
            body: assignmentData,
            auth: true,
          });
          return response;
        });

        return result;
      } catch (error) {
        // Error handling - log to monitoring service in production
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate relevant queries for real-time updates
      // refetchType 'all' — see the note in useAssignLead. Marking a query stale
      // does nothing for it if its observer is unmounted behind a modal.
      const all = { refetchType: "all" as const };
      queryClient.invalidateQueries({ queryKey: ["unassignedLeads"], ...all });
      queryClient.invalidateQueries({ queryKey: ["allLeads"], ...all });
      queryClient.invalidateQueries({ queryKey: ["agentWorkload"], ...all });
      queryClient.invalidateQueries({ queryKey: ["status-tracking"], ...all });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
  });
};
